import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Sparkles, X, RefreshCw, Send, Square } from 'lucide-react';

const ANCHOR_RE = /\s*\[\[node:([a-zA-Z0-9_-]+)\]\]/g;

/**
 * Parse [[node:id]] anchors out of streamed markdown.
 * Returns: { stripped, anchors }
 *   stripped: markdown with anchors removed
 *   anchors: ordered list of node ids in document order (first → step 1, etc.)
 *
 * Robust to anchors that appear mid-paragraph (the model sometimes does that)
 * — we just collect them in document order.
 */
function parseAnchors(content) {
  if (!content) return { stripped: '', anchors: [] };
  const anchors = [];
  const stripped = content.replace(ANCHOR_RE, (_, id) => {
    anchors.push(id);
    return '';
  });
  return { stripped, anchors };
}

/**
 * Hash the parts of definition that affect the explanation. Keeps a cheap
 * "definition changed" signal — we don't need a deep equal, just a value
 * that flips when the user edits the workflow.
 */
function hashDefinition(definition) {
  if (!definition) return '';
  const nodes = definition.nodes || [];
  const edges = definition.edges || [];
  const configs = definition.nodeConfigs || {};
  const nodeKey = nodes
    .map((n) => `${n.id}:${(n.data && n.data.nodeType) || ''}:${(n.data && n.data.label) || ''}`)
    .join('|');
  const edgeKey = edges
    .map((e) => `${e.source}->${e.target}@${e.sourceHandle || ''}`)
    .join('|');
  const configKey = JSON.stringify(configs);
  return `${nodeKey}__${edgeKey}__${configKey}`;
}

const SUGGESTIONS = [
  'What could go wrong with this workflow?',
  'How would I add a Slack notification at the end?',
  'What data does each step produce?',
];

export default function ExplainPanel({
  workflowDefinition,
  explainStream,
  onClose,
  onHighlightNode,
  focusNodeId = null,
}) {
  const {
    messages,
    isStreaming,
    error,
    explainedHash,
    setExplainedHash,
    sendMessage,
    stopStreaming,
    clearMessages,
  } = explainStream;

  const [followup, setFollowup] = useState('');
  const initialFiredRef = useRef(false);
  const scrollRef = useRef(null);

  const currentHash = useMemo(
    () => hashDefinition(workflowDefinition),
    [workflowDefinition]
  );
  const definitionChanged =
    !!explainedHash &&
    currentHash !== explainedHash &&
    !isStreaming &&
    messages.length > 0;

  // Auto-fire the first explanation on mount, but only if there's no
  // existing conversation (the hook is lifted to WorkflowBuilder, so the
  // messages array survives panel close/reopen).
  useEffect(() => {
    if (initialFiredRef.current) return;
    initialFiredRef.current = true;
    if (messages.length > 0 || isStreaming) return;
    setExplainedHash(currentHash);
    sendMessage('', { focusNodeId, suppressUserBubble: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll to bottom while streaming.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  const handleRefresh = () => {
    if (isStreaming) return;
    clearMessages();
    requestAnimationFrame(() => {
      setExplainedHash(currentHash);
      sendMessage('', { focusNodeId, suppressUserBubble: true });
    });
  };

  const handleSendFollowup = (text) => {
    const trimmed = (text || followup).trim();
    if (!trimmed || isStreaming) return;
    setFollowup('');
    sendMessage(trimmed);
  };

  const assistantMessages = messages.filter((m) => m.role === 'assistant');
  const lastAssistant = assistantMessages[assistantMessages.length - 1];
  const showSuggestions =
    !isStreaming &&
    lastAssistant &&
    lastAssistant.content &&
    !error;

  return (
    <div className="w-[380px] flex flex-col flex-shrink-0 border-l border-gray-100 bg-white overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-6 h-6 rounded-md bg-violet-50 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-3.5 h-3.5 text-violet-600" strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <div className="text-[13px] font-medium text-gray-800">Explain</div>
            <div className="text-[11px] text-gray-400">Powered by Able</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isStreaming}
            title="Refresh explanation"
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={onClose}
            title="Close"
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Definition-changed pill */}
      {definitionChanged && (
        <div className="px-4 py-2 border-b border-amber-100 bg-amber-50/60">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[12px] text-amber-900">
              Workflow has changed since this explanation.
            </span>
            <button
              type="button"
              onClick={handleRefresh}
              className="text-[12px] font-medium text-amber-900 underline underline-offset-2 hover:text-amber-700"
            >
              Refresh
            </button>
          </div>
        </div>
      )}

      {/* Body */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
        {assistantMessages.length === 0 && !isStreaming && (
          <div className="text-center py-8">
            <p className="text-[13px] text-gray-500">
              Add a step to get an explanation.
            </p>
          </div>
        )}

        {messages.map((m) => (
          <ExplainMessage
            key={m.id}
            message={m}
            onHighlightNode={onHighlightNode}
          />
        ))}
      </div>

      {/* Suggestions */}
      {showSuggestions && (
        <div className="px-4 pb-2 flex-shrink-0">
          <div className="flex flex-wrap gap-1.5">
            {SUGGESTIONS.map((s) => (
              <button
                type="button"
                key={s}
                onClick={() => handleSendFollowup(s)}
                className="text-[11.5px] text-gray-600 hover:text-gray-800 hover:bg-gray-50 border border-gray-200 rounded-full px-2.5 py-1 transition"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="border-t border-gray-100 px-3 py-2.5 flex-shrink-0">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSendFollowup();
          }}
          className="flex items-center gap-2"
        >
          <input
            type="text"
            value={followup}
            onChange={(e) => setFollowup(e.target.value)}
            placeholder={isStreaming ? 'Streaming…' : 'Ask a follow-up'}
            disabled={isStreaming}
            className="flex-1 text-[13px] bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 disabled:opacity-60"
          />
          {isStreaming ? (
            <button
              type="button"
              onClick={stopStreaming}
              title="Stop"
              className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            >
              <Square className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!followup.trim()}
              title="Send"
              className="p-1.5 rounded-md text-violet-600 hover:bg-violet-50 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Send className="w-3.5 h-3.5" />
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

function ExplainMessage({ message, onHighlightNode }) {
  const isUser = message.role === 'user';
  const { stripped, anchors } = useMemo(
    () => parseAnchors(message.content || ''),
    [message.content]
  );

  if (isUser) {
    return (
      <div className="my-3 flex justify-end">
        <div className="max-w-[280px] bg-gray-100 text-gray-800 rounded-2xl px-3 py-1.5 text-[13px]">
          {message.content}
        </div>
      </div>
    );
  }

  // Counter local to this render; the h3 component closure shares it. New
  // closures are built per render, so concurrent re-renders don't collide.
  let headingIdx = 0;

  return (
    <div className="text-[13.5px] leading-[1.65] text-gray-800 [&>*:first-child]:mt-0">
      {stripped ? (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            p({ children }) {
              return <p className="my-2.5">{children}</p>;
            },
            h1({ children }) {
              return (
                <h1 className="text-base font-semibold mt-4 mb-1.5 text-gray-900">
                  {children}
                </h1>
              );
            },
            h2({ children }) {
              return (
                <h2 className="text-[14px] font-semibold mt-4 mb-1.5 text-gray-900">
                  {children}
                </h2>
              );
            },
            h3({ children }) {
              const nodeId = anchors[headingIdx++];
              if (!nodeId) {
                return (
                  <h3 className="text-[13.5px] font-semibold mt-3.5 mb-1 text-gray-900">
                    {children}
                  </h3>
                );
              }
              return (
                <h3 className="mt-3.5 mb-1">
                  <button
                    type="button"
                    onClick={() => onHighlightNode && onHighlightNode(nodeId)}
                    className="text-left text-[13.5px] font-semibold text-gray-900 hover:text-violet-700 transition group inline-flex items-baseline gap-1"
                  >
                    {children}
                    <span className="text-[10px] uppercase tracking-wide text-violet-500 opacity-0 group-hover:opacity-100 transition">
                      view step
                    </span>
                  </button>
                </h3>
              );
            },
            ul({ children }) {
              return (
                <ul className="my-2 ml-1 space-y-1 list-disc list-outside pl-5 marker:text-gray-400">
                  {children}
                </ul>
              );
            },
            ol({ children }) {
              return (
                <ol className="my-2 ml-1 space-y-1 list-decimal list-outside pl-5 marker:text-gray-400">
                  {children}
                </ol>
              );
            },
            li({ children }) {
              return <li className="pl-0.5">{children}</li>;
            },
            strong({ children }) {
              return <strong className="font-semibold text-gray-900">{children}</strong>;
            },
            code({ children }) {
              return (
                <code className="bg-gray-100 px-1.5 py-0.5 rounded-md text-[12px] font-mono text-gray-800 border border-gray-200/60">
                  {children}
                </code>
              );
            },
            a({ children, ...props }) {
              return (
                <a
                  {...props}
                  className="text-violet-700 underline underline-offset-2 decoration-violet-300 hover:decoration-violet-500"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {children}
                </a>
              );
            },
          }}
        >
          {stripped}
        </ReactMarkdown>
      ) : null}

      {message.isStreaming && !stripped && (
        <div className="flex items-center gap-1.5 py-1">
          <div
            className="w-1.5 h-1.5 rounded-full bg-gray-300 animate-bounce"
            style={{ animationDelay: '0ms' }}
          />
          <div
            className="w-1.5 h-1.5 rounded-full bg-gray-300 animate-bounce"
            style={{ animationDelay: '150ms' }}
          />
          <div
            className="w-1.5 h-1.5 rounded-full bg-gray-300 animate-bounce"
            style={{ animationDelay: '300ms' }}
          />
        </div>
      )}
      {message.isStreaming && stripped && (
        <span className="inline-block w-1.5 h-3.5 bg-gray-400 animate-pulse ml-0.5 align-middle" />
      )}
    </div>
  );
}
